import * as React from 'react';
import { Layout, Row, Col, Card, Button, Divider, message, Popover } from 'antd';
import { Document, Page } from 'react-pdf';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { PageLoadingPlaceholder } from '../page_loading_placeholder/PageLoadingPlaceholder';
import {
	downloadInvoicePDFForSupplier,
	invoicesRestCrudThunksForSupplier,
	markInvoiceDraftForSupplier,
	markInvoicePaidForSupplier,
	publishInvoiceForSupplier,
} from '../../thunks/invoices_thunks';
import { dateFormatter, getTimeDurationFromSeconds } from '../../utils/DataFormatterUtils';
import {
	getCurrency,
	getEntityById,
	isInvoiceTotalGreaterThanNTE,
	nullSafeGet,
	nullSafeGetOrElse,
} from '../../utils/DataAccessUtils';
import { getRecordsForTargetCollection } from '../../reducers/standard_reducer_utils';
import { EmptyState } from '../empty_state/EmptyState';
import { Link } from 'react-router-dom';
import LogOnMountWithStandardEventProperties from '../log_on_mount_with_standard_event_properties/LogOnMountWithStandardEventProperties';
import InvoiceUploader from '../invoice_uploader/InvoiceUploader';
import { INVOICE_STATUSES } from '../../constants/InvoiceStatuses';
import {
	appendWorkOrderNotesForBuyer,
	appendWorkOrderNotesForSupplier,
	workOrdersRestCrudThunksForSupplier,
} from '../../thunks/work_orders_thunks';
import { WORK_ORDER_STATUSES } from '../../constants/WorkOrderStatuses';
import InvoicesAdditionalDetails from '../invoices_components/InvoicesAdditionalDetails';
import { SUPER_ADMIN, SUPPLIER_ADMIN } from '../roles/roles';
import { UndoOutlined } from '@ant-design/icons';
import { UserName } from '../name_component/user/UserNameComponent';
import moment from 'moment';
import AccessPermissionChecker from '../common/AccessPermissionChecker';
import { PERMISSION_NAMES } from '../../utils/AuthUtils';
import InvoiceActions from '../common/InvoiceActions';

const { Content } = Layout;

const style = require('./SupplierWorkOrdersDetailInvoicesPage.less');
const dateFormat = 'MMM D, YYYY';

class SupplierWorkOrderInvoicesDetailPage extends React.Component<any, any> {
	constructor(props) {
		super(props);
		this.state = {
			generatingPDF: false,
			pdfFile: { url: null, httpHeaders: null },
			isGreaterThanNTE: false,
			invoiceExists: false,
		};
	}

	refreshInvoice = () => {
		const { getInvoices, match } = this.props;
		const workOrderId = match.params.id;
		getInvoices({ workOrderId }, 'workOrderAssociatedInvoices');
	};

	generateInvoicePDF(id) {
		this.setState({ generatingPDF: true });
		downloadInvoicePDFForSupplier(id).finally(() => {
			this.setState({ generatingPDF: false });
		});
	}

	componentDidMount() {
		const { getWorkOrder, getInvoices, match, getCount } = this.props;
		const workOrderId = match.params.id;
		getInvoices({ workOrderId }, 'workOrderAssociatedInvoices');
		getCount(workOrderId, 'draft').then((d) => this.setState({ invoiceExists: d > 0 }));
	}

	handlePublishInvoice = (invoice) => {
		const { publishInvoice } = this.props;
		const nte = nullSafeGet('workOrder.nte', invoice);
		if (invoice > nte) {
			this.setState({ isGreaterThanNTE: true });
		} else {
			publishInvoice(invoice.id).then(() => {
				message.success(`Invoice published.`);
			});
		}
	};

	handlePaidInvoice = (invoice) => {
		const { markInvoicePaid } = this.props;
		markInvoicePaid(invoice.id).then(() => {
			message.success(`Invoice paid.`);
		});
	};

	handleFileAttachmentUploadChange = (invoice) => (newAttachments) => {
		const { updateInvoice } = this.props;
		const newInvoice = {
			...invoice,
			attachments: newAttachments,
		};
		updateInvoice(newInvoice);
	};

	handleInvoicePDFUploadChange = (invoice) => (invoiceFileUrl) => {
		const { updateInvoice, getInvoices, match } = this.props;
		const newInvoice = {
			...invoice,
			invoicePDFLink: invoiceFileUrl,
		};
		updateInvoice(newInvoice).then(() => {
			const workOrderId = match.params.id;
			getInvoices({ workOrderId }, 'workOrderAssociatedInvoices');
		});
	};

	onDocumentLoadSuccess = ({ numPages }) => {
		this.setState({ numPages });
	};

	handleMoveInvoiceToDraft = (invoiceId) => () => {
		const { markInvoiceDraft } = this.props;
		this.setState({ buttonLoading: true });
		markInvoiceDraft(invoiceId)
			.then(() => {
				message.success(`Invoice moved to draft.`);
			})
			.finally(() => this.setState({ buttonLoading: false }));
	};

	render() {
		const {
			workOrder,
			invoices,
			invoicesFetching,
			currentUser,
			invoicesCreating,
			history,
			match,
			appendWorkOrderNotes,
		} = this.props;
		const workOrderId = match.params.id;
		const isInternalTech = nullSafeGetOrElse('facility.status', currentUser, '') === 'private';
		const isAdmin =
			currentUser.roles.includes(SUPPLIER_ADMIN) || currentUser.roles.includes(SUPER_ADMIN);

		const getInvoiceWarningText = (workOrder) => {
			switch (workOrder.status) {
				case WORK_ORDER_STATUSES.pendingConfirmationByServiceProvider:
					return "You haven't accepted this work order yet. You need to accept and complete the work order before you can submit a bill.";
				case WORK_ORDER_STATUSES.confirmedByServiceProvider:
					return "You haven't scheduled a service call for this work order yet. You need to schedule and complete the work order before you can submit a bill.";
				case WORK_ORDER_STATUSES.techAssigned:
					return "You haven't completed a service call for this work order yet. You need to complete the work order before you can submit a bill.";
				case WORK_ORDER_STATUSES.techScheduled:
					return "You haven't completed a service call for this work order yet. You need to complete the work order before you can submit a bill.";
				case WORK_ORDER_STATUSES.techRescheduled:
					return "You haven't completed a service call for this work order yet. You need to complete the work order before you can submit a bill.";
				case WORK_ORDER_STATUSES.techEnRoute:
					return "You haven't completed a service call for this work order yet. You need to complete the work order before you can submit a bill.";
				case WORK_ORDER_STATUSES.techWaitingOnSite:
					return "You haven't completed a service call for this work order yet. You need to complete the work order before you can submit a bill.";
				case WORK_ORDER_STATUSES.techWorkingOnSite:
					return "You haven't completed a service call for this work order yet. You need to complete the work order before you can submit a bill.";
				case WORK_ORDER_STATUSES.workIncompleteWithReason:
					return 'You need to complete the work order before you can submit a bill.';
				case WORK_ORDER_STATUSES.workUnsatisfactory:
					return 'The client has disputed that the issue is resolved and work is complete. You need to complete the work order before you can submit a bill.';
				case WORK_ORDER_STATUSES.waitingForReview:
					return "The client hasn't yet confirmed that work is complete. They need to review your work and confirm work is done before you can submit a bill.";
				case WORK_ORDER_STATUSES.waitingForQuote:
					return 'The work order is waiting on a proposal to be submitted. Work needs to be completed before you can submit a bill.';
				case WORK_ORDER_STATUSES.waitingForProposal:
					return 'The work order is waiting on a proposal to be submitted. Work needs to be completed before you can submit a bill.';
				case WORK_ORDER_STATUSES.waitingForProposalApproval:
					return "Your proposal on this work order hasn't been approved yet. Work needs to be completed before you can submit a bill.";
				case WORK_ORDER_STATUSES.proposalApproved:
					return "You haven't completed a service call for this work order yet. You need to complete the work order before you can submit a bill.";
				case WORK_ORDER_STATUSES.partsOnOrder:
					return "You haven't completed a service call for this work order yet. You need to complete the work order before you can submit a bill.";
				case WORK_ORDER_STATUSES.partsReceived:
					return "You haven't completed a service call for this work order yet. You need to complete the work order before you can submit a bill.";
				case WORK_ORDER_STATUSES.partsRequested:
					return "You haven't completed a service call for this work order yet. You need to complete the work order before you can submit a bill.";
				case WORK_ORDER_STATUSES.delayedByThirdParty:
					return 'You need to complete the work order before you can submit a bill.';
				default:
					return null;
			}
		};

		const supplierFacilityId = nullSafeGet('facility.id', currentUser);

		const associatedInvoices = getRecordsForTargetCollection(
			invoices,
			'workOrderAssociatedInvoices'
		);

		const disableSimpleInvoiceEntry = nullSafeGetOrElse(
			'buyerCompany.buyerCompanySettings.config.suppliersConfig.disableSimpleInvoiceEntry',
			workOrder,
			false
		);

		const isEmptyState =
			associatedInvoices.some((invoice) => invoice.status === 'draft') && isInternalTech;

		if (isEmptyState) {
			return (
				<EmptyState
					graphic={
						<img
							style={{ marginBottom: 8 }}
							src="https://s3.amazonaws.com/mock-data-assets/categories/images/bank terminal.svg"
							alt=""
						/>
					}
					headline={
						!isInternalTech
							? "You haven't billed for this work order yet."
							: "You haven't been billed for this work order yet."
					}
					body={
						!isInternalTech ? (
							<div style={{ textAlign: 'center' }}>
								<Link
									to={`/supplier/workOrders/detail/${workOrderId}/invoices/new`}
									style={{ marginRight: 8 }}
								>
									<Button type="primary">Create invoice</Button>
								</Link>
								{!disableSimpleInvoiceEntry && (
									<Link
										to={`/supplier/workOrders/detail/${workOrderId}/invoices/createSimple`}
										style={{ marginRight: 8 }}
									>
										<Button>Upload invoice PDF</Button>
									</Link>
								)}
							</div>
						) : null
					}
				/>
			);
		}

		return invoicesFetching ? (
			<PageLoadingPlaceholder />
		) : (
			<Content className="invoicesDetailDetailsPage">
				<LogOnMountWithStandardEventProperties eventType="visited supplier work order invoices page" />
				{/*<ScrollToTopOnMount/>*/}
				{/*<BackTop/>*/}
				{associatedInvoices.length > 0 ? (
					<div>
						{this.state.pdfFile.url ? (
							<div>
								<Document file={this.state.pdfFile} onLoadSuccess={this.onDocumentLoadSuccess}>
									{Array.from(new Array(this.state.numPages), (el, index) => (
										<Page width={1100} key={`page_${index + 1}`} pageNumber={index + 1} />
									))}
								</Document>
								<p>
									Page {this.state.pageNumber} of {this.state.numPages}
								</p>
							</div>
						) : null}
						{associatedInvoices.map((invoice) => {
							const nte = nullSafeGet('workOrder.nte', invoice);
							const invoiceId = nullSafeGet('id', invoice);
							const totalIsGreaterThanNTE = isInvoiceTotalGreaterThanNTE(
								invoice,
								nullSafeGet('workOrder', invoice)
							);
							const isBuyerInvoice = invoice.isBuyerUploaded || false;
							const hasPDF = invoice.invoicePDFLink && invoice.invoicePDFLink.length > 0;
							const currency = getCurrency({ workOrder: nullSafeGet('workOrder', invoice) });
							const pdfElement = hasPDF ? (
								<p className={'mb-1'}>
									<a href={invoice.invoicePDFLink} target="_blank" rel="noreferrer">
										Invoice PDF
									</a>
								</p>
							) : null;
							const uploadInvoice = isAdmin ? (
								[INVOICE_STATUSES.draft, INVOICE_STATUSES.disputed].some(
									(status) => status === invoice.status
								) ? (
									<InvoiceUploader
										roleType="buyer"
										invoiceId={invoice.id}
										defaultFileList={[]}
										multiple={false}
										handleUploadSuccess={this.handleInvoicePDFUploadChange}
									/>
								) : invoice.status === INVOICE_STATUSES.pending ? (
									<Popover
										content={
											isInternalTech
												? 'Invoices must be disputed status to allow upload of a pdf.'
												: 'Invoice must be moved back to draft status to allow upload of a pdf.'
										}
										placement={'left'}
									>
										<div>
											<InvoiceUploader
												disabled={true}
												roleType="buyer"
												invoiceId={invoice.id}
												defaultFileList={[]}
												multiple={false}
												handleUploadSuccess={this.handleInvoicePDFUploadChange}
											/>
										</div>
									</Popover>
								) : null
							) : null;

							const lastDisputeNote = invoice.disputeNotes && invoice.disputeNotes.slice(-1)[0];
							const disputeNote = lastDisputeNote ? (
								<Col>
									<div>
										<span style={{ fontSize: 16 }}>
											Disputed by{' '}
											<UserName
												mode={'inline'}
												contact={nullSafeGet(`noteAddedByContact`, lastDisputeNote)}
											/>{' '}
											{moment(nullSafeGet(`noteAddedAt`, lastDisputeNote)).fromNow()}
										</span>
									</div>
									<div
										style={{
											display: 'inline-flex',
											background: '#fafafa',
											color: 'rgba(0, 0, 0, 0.65)',
											borderRadius: '30px',
											padding: '8px 16px',
										}}
									>
										<span
											style={{
												fontSize: 16,
											}}
										>
											{nullSafeGet(`text`, lastDisputeNote)}
										</span>
									</div>
									{!isInternalTech ? (
										<div className={'mt-2 flex gap-4'}>
											<Button
												type="primary"
												key="Edit"
												onClick={() => history.push(`/supplier/invoices/edit/${invoiceId}`)}
											>
												Edit Invoice
											</Button>
											<Button
												key="Note"
												onClick={() =>
													history.push(`/supplier/workOrders/detail/${invoice.workOrderId}/notes`)
												}
											>
												Send a note
											</Button>
										</div>
									) : null}
								</Col>
							) : null;

							const generateInvoice = !isInternalTech ? (
								<p style={{ marginBottom: 0, marginTop: 0 }}>
									Don't have an invoice file?{' '}
									<Button
										loading={this.state.generatingPDF}
										type="link"
										onClick={() => this.generateInvoicePDF(invoice.id)}
										style={{ marginBottom: 0, marginTop: 0 }}
									>
										Generate PDF
									</Button>
								</p>
							) : null;
							return (
								<Row style={{ margin: '16px 8px' }} gutter={12} key={invoice.id}>
									<Col span={24}>
										{!isInternalTech ? (
											invoice.status === 'draft' && totalIsGreaterThanNTE ? (
												<div className="rowSpacing">
													<Card
														style={{ border: '1px solid #8ed7eb' }}
														bodyStyle={{
															padding: '16px 24px',
															background: '#ebfbff',
														}}
													>
														<div>
															<h5 style={{ marginBottom: '4px' }}>
																Total is greater than NTE Amount
															</h5>
															<div>
																<p
																	style={{
																		fontSize: 16,
																		marginBottom: '0.5em',
																	}}
																>
																	You can't submit an Invoice for more than the not-to-exceed amount{' '}
																	{currency.format(nte)} for this work order. Please submit a Quote
																	to increase not-to-exceed amount or edit this invoice.
																</p>
															</div>
															<Button
																type="primary"
																key="approve"
																onClick={() => history.push(`/supplier/invoices/edit/${invoiceId}`)}
															>
																Edit Invoice
															</Button>
														</div>
													</Card>
												</div>
											) : invoice.status === 'pending' && totalIsGreaterThanNTE ? (
												<div className="rowSpacing">
													<Card
														style={{ border: '1px solid #8ed7eb' }}
														bodyStyle={{
															padding: '16px 24px',
															background: '#ebfbff',
														}}
													>
														<div>
															<h5 style={{ marginBottom: '4px' }}>
																Total is greater than NTE Amount
															</h5>
															<div>
																<p
																	style={{
																		fontSize: 16,
																		marginBottom: '0.5em',
																	}}
																>
																	{nullSafeGet('buyerCompany.displayName', workOrder)} can't approve
																	because the invoice is for more than the not-to-exceed amount{' '}
																	{currency.format(nte)} for this work order. Please submit a Quote
																	to increase not-to-exceed amount or edit this invoice.
																</p>
															</div>
															<Button
																type="primary"
																key="approve"
																onClick={() => history.push(`/supplier/invoices/edit/${invoiceId}`)}
															>
																Edit Invoice
															</Button>
														</div>
													</Card>
												</div>
											) : invoice.status === 'draft' &&
											  invoice.invoicePDFLink &&
											  invoice.invoicePDFLink.length > 0 ? (
												workOrder.displayStatus === 'Completed' ? (
													<div className="rowSpacing">
														<Card
															style={{ border: '1px solid #8ed7eb' }}
															bodyStyle={{
																padding: '16px 24px',
																background: '#ebfbff',
															}}
														>
															<div>
																<h5 style={{ marginBottom: '4px' }}>Publish invoice?</h5>
																<div>
																	<p
																		style={{
																			fontSize: 16,
																			marginBottom: '0.5em',
																		}}
																	>
																		This invoice is still a draft and isn't visible to the customer
																		yet.
																	</p>
																	<Button
																		type="primary"
																		key="approve"
																		loading={invoicesCreating}
																		onClick={() => this.handlePublishInvoice(invoice)}
																	>
																		Publish invoice
																	</Button>
																</div>
															</div>
														</Card>
													</div>
												) : (
													<div className="rowSpacing">
														<Card
															style={{ border: '1px solid #ffccc7' }}
															bodyStyle={{
																padding: '16px 24px',
																background: '#fff2f0',
															}}
														>
															<div>
																<h5 style={{ marginBottom: '4px' }}>
																	You can't publish this invoice until the work is completed.
																</h5>
																<div>
																	<p
																		style={{
																			fontSize: 16,
																			marginBottom: '0.5em',
																		}}
																	>
																		This invoice is still a draft and isn't visible to the customer
																		yet. {getInvoiceWarningText(workOrder)}
																	</p>
																	<Button
																		type="primary"
																		key="approve"
																		disabled={true}
																		loading={invoicesCreating}
																		onClick={() => this.handlePublishInvoice(invoice)}
																	>
																		Publish invoice
																	</Button>
																</div>
															</div>
														</Card>
													</div>
												)
											) : null
										) : null}
										{!isInternalTech &&
										invoice.status === 'draft' &&
										(!invoice.invoicePDFLink || invoice.invoicePDFLink.length === 0) ? (
											<div className="rowSpacing">
												<Card
													style={{ border: '1px solid #8ed7eb' }}
													bodyStyle={{
														padding: '16px 24px',
														background: '#ebfbff',
													}}
												>
													<div>
														<h5 style={{ marginBottom: '4px' }}>Upload an invoice file</h5>
														<div>
															<p
																style={{
																	fontSize: 16,
																	marginBottom: '0.5em',
																}}
															>
																Please add an invoice file (PDF) for this invoice before submitting
																it.
															</p>
															<InvoiceUploader
																roleType="supplier"
																invoiceId={invoice.id}
																buttonType="primary"
																defaultFileList={[]}
																multiple={false}
																handleUploadSuccess={this.handleInvoicePDFUploadChange(invoice)}
															/>
															<p style={{ marginBottom: 0, marginTop: 8 }}>
																Don't have an invoice file?{' '}
																<Button
																	loading={this.state.generatingPDF}
																	type="link"
																	onClick={() => this.generateInvoicePDF(invoice.id)}
																>
																	Generate PDF
																</Button>
															</p>
														</div>
													</div>
												</Card>
											</div>
										) : null}
										<Card>
											{isInternalTech ? (
												<InvoiceActions
													invoice={invoice}
													refreshInvoice={this.refreshInvoice}
													updateInvoice={this.props.updateInvoice}
													style={style}
													appendWorkOrderNotes={appendWorkOrderNotes}
												/>
											) : null}
											<Row justify={'space-between'}>
												{!isInternalTech && invoice.status === 'approved' ? (
													<div>
														<h5>Has this invoice been paid?</h5>
														<div style={{ marginTop: 16, marginBottom: 40 }}>
															<Button
																type="primary"
																key="approve"
																onClick={() => this.handlePaidInvoice(invoice)}
															>
																Yes, it was paid
															</Button>
														</div>
													</div>
												) : !isInternalTech && invoice.status === 'pending' ? (
													<div>
														<div style={{ marginTop: 16, marginBottom: 40 }}>
															<h5>This invoice is pending approval.</h5>
															<Button
																type="default"
																key="draft"
																icon={<UndoOutlined translate="" />}
																loading={this.state.buttonLoading}
																onClick={this.handleMoveInvoiceToDraft(invoice.id)}
															>
																Move Back To Draft
															</Button>
														</div>
													</div>
												) : (
													<div />
												)}
												<Button
													style={{ marginLeft: 16 }}
													onClick={() => {
														history.push(`/supplier/invoices/detail/${invoice.id}`);
													}}
												>
													View Invoice Page
												</Button>
											</Row>
											{!isInternalTech ? (
												<div
													style={{
														marginBottom: 24,
														display: 'flex',
														justifyContent: 'space-between',
														alignItems: 'center',
													}}
												>
													<Link to={`/supplier/invoices/detail/${invoice.id}`}>
														<h5 style={{ marginBottom: 0 }}>Invoice {invoice.id}</h5>
													</Link>
													<div>
														{!isInternalTech &&
														(invoice.status === 'draft' || invoice.status === 'disputed') ? (
															<Button
																onClick={() => {
																	history.push(
																		`/supplier/workOrders/detail/${workOrderId}/invoices/${invoice.id}/edit?btwo=true`
																	);
																}}
															>
																Edit
															</Button>
														) : null}
													</div>
												</div>
											) : null}
											{invoice.status === 'disputed' ? disputeNote : null}
											{/*{invoice.status === 'pending' ? (*/}
											{/*<div>*/}
											{/*<h6>*/}
											{/*This invoice has been submitted and is pending by the client.*/}
											{/*</h6>*/}
											{/*</div>*/}
											{/*) : (*/}
											{/*null*/}
											{/*)}*/}
											<InvoicesAdditionalDetails
												invoice={invoice}
												passthroughItems={[
													pdfElement || uploadInvoice
														? {
																key: 'File',
																value: (
																	<div>
																		{pdfElement}
																		{uploadInvoice}
																		{generateInvoice}
																	</div>
																),
																className: 'detailRow__item firstRow lastCol',
														  }
														: {},
													{
														key: 'Submitted By Company',
														value: isBuyerInvoice
															? nullSafeGet('buyerCompany.displayName', workOrder)
															: nullSafeGet('supplierFacility.displayName', workOrder),
													},
													{
														key: 'Submitted On',
														value: dateFormatter(invoice.createdAt, dateFormat),
													},
												]}
											/>
											<Divider />
											<div className="sectionHeaderWrapper" style={{ marginBottom: 8 }}>
												<span className="sectionHeaderText">Labor</span>
												<span className="sectionHeaderText2">
													{currency.format(invoice.laborTotalBeforeTax)}
												</span>
											</div>
											{nullSafeGetOrElse('laborLineItems', invoice, []).map((lli, idx) => (
												<div
													className="sectionHeaderWrapper2"
													style={{
														marginBottom:
															idx === nullSafeGetOrElse('laborLineItems', invoice, []).length - 1
																? 16
																: 8,
													}}
												>
													<span className="lineItemDetailText">
														{lli.numberOfTech} {lli.roleType} *{' '}
														{getTimeDurationFromSeconds(lli.seconds)} @{' '}
														{currency.format(parseFloat(lli.hourlyRate))} / hour
													</span>
													<span className="lineItemDetailText">
														{currency.format(parseFloat(lli.amount))}
													</span>
												</div>
											))}
											<Divider />

											<div className="sectionHeaderWrapper" style={{ marginBottom: 8 }}>
												<span className="sectionHeaderText">Materials</span>
												<span className="sectionHeaderText2">
													{currency.format(invoice.materialTotalBeforeTax)}
												</span>
											</div>
											{nullSafeGetOrElse('materialLineItems', invoice, []).map((mli, idx) => (
												<div
													className="sectionHeaderWrapper2"
													style={{
														marginBottom:
															idx === nullSafeGetOrElse('laborLineItems', invoice, []).length - 1
																? 16
																: 8,
													}}
												>
													<span className="lineItemDetailText">
														{mli.quantity} {mli.unitType} of {mli.description}
														{mli.partNumber ? ` (${mli.partNumber})` : null} @{' '}
														{currency.format(parseFloat(mli.unitPrice))} / {mli.unitType}
													</span>
													<span className="lineItemDetailText">
														{currency.format(parseFloat(mli.amount))}
													</span>
												</div>
											))}
											<Divider />
											<div className="sectionHeaderWrapper">
												<span className="sectionHeaderText">Travel</span>
												<span className="sectionHeaderText2">
													{currency.format(parseFloat(invoice.travelTotalBeforeTax) || 0)}
												</span>
											</div>
											<Divider />
											<div className="sectionHeaderWrapper">
												<span className="sectionHeaderText">Freight</span>
												<span className="sectionHeaderText2">
													{currency.format(parseFloat(invoice.freightTotalBeforeTax) || 0)}
												</span>
											</div>
											<Divider />
											<div className="sectionHeaderWrapper">
												<span className="sectionHeaderText">Miscellaneous</span>
												<span className="sectionHeaderText2">
													{currency.format(parseFloat(invoice.miscTotalBeforeTax) || 0)}
												</span>
											</div>
											{nullSafeGetOrElse('miscLineItems', invoice, []).map((mli, idx) => (
												<div
													className="sectionHeaderWrapper2"
													style={{
														marginBottom:
															idx === nullSafeGetOrElse('miscLineItems', invoice, []).length - 1
																? 16
																: 8,
													}}
												>
													<span className="lineItemDetailText">{mli.description}</span>
													<span className="lineItemDetailText">
														{currency.format(parseFloat(mli.amount))}
													</span>
												</div>
											))}
											<Divider />
											<div className="sectionHeaderWrapper">
												<span className="sectionHeaderText">Total Before Tax</span>
												<span className="sectionHeaderText2">
													{currency.format(invoice.invoiceTotalBeforeTax)}
												</span>
											</div>
											<Divider />
											<div className="sectionHeaderWrapper">
												<span className="sectionHeaderText">Tax</span>
												<span className="sectionHeaderText2">
													{currency.format(invoice.invoiceTax)}
												</span>
											</div>
											{nullSafeGetOrElse('taxLineItems', invoice, []).map((lli, idx) => (
												<div
													className="sectionHeaderWrapper2"
													style={{
														marginBottom:
															idx === nullSafeGetOrElse('taxLineItems', invoice, []).length - 1
																? 16
																: 8,
													}}
												>
													<span className="lineItemDetailText">{lli.taxType}</span>
													<span className="lineItemDetailText">
														{currency.format(parseFloat(lli.amount))}
													</span>
												</div>
											))}
											<Divider />
											<div className="sectionHeaderWrapper">
												<span className="sectionHeaderText" style={{ fontWeight: 600 }}>
													Total After Tax
												</span>
												<span className="sectionHeaderText2" style={{ fontWeight: 600 }}>
													{currency.format(invoice.invoiceTotalAfterTax)}
												</span>
											</div>
										</Card>
									</Col>
								</Row>
							);
						})}
					</div>
				) : (
					<EmptyState
						graphic={
							<img
								style={{ marginBottom: 8 }}
								src="https://s3.amazonaws.com/mock-data-assets/categories/images/bank terminal.svg"
								alt=""
							/>
						}
						headline={
							!isInternalTech
								? "You haven't billed for this work order yet."
								: "You haven't been billed for this work order yet."
						}
						body={
							!isInternalTech ||
							(nullSafeGet('supplierFacilityId', workOrder) &&
								supplierFacilityId !== workOrder.supplierFacilityId) ? (
								this.state.invoiceExists ? (
									<span>
										Your service provider has drafted an invoice, it just hasn't been published yet.
									</span>
								) : (
									<AccessPermissionChecker
										name={PERMISSION_NAMES.CAN_UPLOAD_INVOICES_FOR_SUPPLIER}
										exclude3rdPartySuppliers
									>
										<div style={{ textAlign: 'center' }}>
											<Link
												to={`/supplier/workOrders/detail/${workOrderId}/invoices/${
													isInternalTech ? 'newForSupplier' : 'new'
												}`}
												style={{ marginRight: 8 }}
											>
												<Button type="primary">Create invoice</Button>
											</Link>
											{disableSimpleInvoiceEntry ? (
												<Popover
													content={`${nullSafeGet(
														'buyerCompany.displayName',
														workOrder
													)} has disabled the simple invoice entry`}
												>
													<Button disabled={true}>Upload invoice PDF</Button>
												</Popover>
											) : (
												<Link
													to={`/supplier/workOrders/detail/${workOrderId}/invoices/createSimple`}
													style={{ marginRight: 8 }}
												>
													<Button>Upload invoice PDF</Button>
												</Link>
											)}
										</div>
									</AccessPermissionChecker>
								)
							) : null
						}
					/>
				)}
			</Content>
		);
	}
}

const mapStateToProps = (state, ownProps) => ({
	workOrdersFetching: state.work_orders.fetching,
	workOrder: getEntityById(ownProps.match.params.id, state.work_orders),
	invoicesFetching: state.invoices.fetching,
	invoices: state.invoices,
	invoicesCreating: state.invoices.creating,
	currentUser: state.session.currentUser,
	match: ownProps.match,
	history: ownProps.history,
});

const mapDispatchToProps = (dispatch) => ({
	getWorkOrder: (id) => dispatch(workOrdersRestCrudThunksForSupplier.readOne(id)),
	getInvoices: (params, targetCollectionName) =>
		dispatch(invoicesRestCrudThunksForSupplier.read(params, targetCollectionName)),
	updateInvoice: (entity) => dispatch(invoicesRestCrudThunksForSupplier.update(entity)),
	markInvoicePaid: (id) => dispatch(markInvoicePaidForSupplier({ id })),
	markInvoiceDraft: (id) => dispatch(markInvoiceDraftForSupplier({ id })),
	publishInvoice: (id) => dispatch(publishInvoiceForSupplier({ id })),
	getCount: (workOrderId, status) =>
		dispatch(invoicesRestCrudThunksForSupplier.countBy({ workOrderId, status }, status)),
	appendWorkOrderNotes: (entity) => dispatch(appendWorkOrderNotesForSupplier(entity)),
});

export default withRouter(
	connect(mapStateToProps, mapDispatchToProps)(SupplierWorkOrderInvoicesDetailPage)
);
